import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquareText, X, Send, Sparkles, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Quale dei miei clienti ha l'EBITDA margin più alto?",
  "Confronta i ricavi YTD di Sciatikalia con l'anno scorso",
  "Suggerisci 3 leve per migliorare il margine di Magic Escape",
  "Quale cliente ha il maggior calo di fatturato negli ultimi mesi?",
];

/**
 * Bottone flottante in basso a destra che apre un pannello chat.
 * - GPT-5.2 con accesso ai dati di TUTTI i clienti dell'utente
 * - Una domanda per volta (no storico)
 * - Possibilità di "focalizzare" su un cliente specifico per dettagli mese-per-mese
 */
export const FloatingAiChat = () => {
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [focusClient, setFocusClient] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/ai/chat/clients`)
      .then(r => setClients(r.data || []))
      .catch(() => {});
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const send = async () => {
    if (!question.trim() || loading) return;
    setLoading(true);
    setAnswer("");
    try {
      const res = await axios.post(`${API}/ai/chat`, {
        question: question.trim(),
        client_id: focusClient || null,
      });
      setAnswer(res.data.answer || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore AI");
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      send();
    }
  };

  const reset = () => { setQuestion(""); setAnswer(""); };

  return (
    <>
      {/* Floating Trigger */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 group flex items-center gap-2 rounded-full bg-gradient-to-br from-violet-600 to-blue-600 text-white shadow-xl hover:shadow-2xl transition-all px-5 py-3.5 hover:-translate-y-0.5"
          data-testid="floating-ai-chat-trigger"
        >
          <MessageSquareText className="h-5 w-5" />
          <span className="text-sm font-medium hidden md:inline">Chiedi all'AI</span>
          <span className="absolute -top-1 -right-1 bg-white text-violet-700 text-[10px] font-bold rounded-full px-1.5 border border-violet-200">
            <Sparkles className="h-2.5 w-2.5 inline" />
          </span>
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div className="fixed inset-0 sm:inset-auto sm:bottom-6 sm:right-6 z-50 sm:w-[440px] sm:max-h-[80vh] bg-white sm:rounded-xl shadow-2xl border-2 border-violet-200 flex flex-col" data-testid="floating-ai-chat-panel">
          {/* Header */}
          <div className="bg-gradient-to-r from-violet-600 to-blue-600 text-white px-4 py-3 sm:rounded-t-xl flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              <div>
                <div className="text-sm font-bold">Assistente AI · GPT-5.2</div>
                <div className="text-[10px] opacity-90">Hai accesso ai dati di tutti i tuoi clienti</div>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)} className="text-white hover:bg-white/20 h-8 w-8 p-0" data-testid="floating-ai-chat-close">
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            <div>
              <label className="text-xs text-slate-600 block mb-1">Focus su un cliente (opzionale)</label>
              <Select value={focusClient || "__all__"} onValueChange={(v) => setFocusClient(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-9 text-xs" data-testid="floating-ai-chat-focus-client">
                  <SelectValue placeholder="Tutti i clienti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutti i clienti (visione globale)</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!answer && !loading && (
              <div className="space-y-1.5">
                <div className="text-[11px] text-slate-500 font-medium">Esempi:</div>
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => setQuestion(s)}
                    className="w-full text-left text-xs px-3 py-2 rounded-md bg-slate-50 hover:bg-violet-50 hover:text-violet-800 border border-slate-200 hover:border-violet-300 transition-all"
                    data-testid={`floating-ai-chat-suggestion-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {loading && (
              <div className="flex items-center justify-center gap-2 py-6 text-slate-500 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Sto elaborando i tuoi dati...
              </div>
            )}

            {answer && !loading && (
              <div className="rounded-lg bg-violet-50 border border-violet-200 p-3 text-sm text-slate-700 leading-relaxed whitespace-pre-line" data-testid="floating-ai-chat-answer">
                {answer}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-200 p-3 bg-slate-50 sm:rounded-b-xl">
            {answer && (
              <Button variant="ghost" size="sm" onClick={reset} className="w-full mb-2 text-xs h-7" data-testid="floating-ai-chat-new">
                Nuova domanda
              </Button>
            )}
            <div className="flex items-end gap-2">
              <Textarea
                ref={inputRef}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={onKey}
                placeholder="Fai una domanda sui tuoi dati... (Ctrl+Invio)"
                rows={2}
                className="resize-none text-sm"
                disabled={loading}
                data-testid="floating-ai-chat-input"
              />
              <Button onClick={send} disabled={loading || !question.trim()} size="sm" className="bg-violet-600 hover:bg-violet-700 h-9 w-9 p-0" data-testid="floating-ai-chat-send">
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <div className="text-[10px] text-slate-400 mt-1">Ogni domanda è indipendente — niente storico conservato.</div>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingAiChat;
